/**
 * cv-newsletter.js
 *
 * Portal-free newsletter flows for cyber/verso.
 *
 * Talks only to Ghost endpoints on the same domain:
 *   GET    /members/api/integrity-token/
 *   POST   /members/api/send-magic-link/
 *   GET    /members/api/member/newsletters/  (uuid + key from the
 *                                             email link, or session)
 *   PUT    /members/api/member/newsletters/
 *   DELETE /members/api/session/
 *
 * Writes no cookies, no Web Storage, contacts no third-party host.
 * Request shapes mirror Portal and Ghost 6.64's members router:
 * re-check them whenever Ghost changes the members API.
 *
 * There is NO <form>: the signup module is a div whose input and
 * button ship disabled and are enabled here at bind time. A form
 * with an email input degrades to a native GET without JavaScript
 * (16-Sep security re-verification) — with no form there is no
 * native submission path at all.
 *
 * The email-flow query parameters (action, uuid, key, …) never
 * reach this script through location.search: the inline allow-list
 * in default.hbs strips them before any deferred/bundled script
 * runs, stashing them in window.__cvNl first. Read them from there.
 *
 * ES5 on purpose: bundled into built/source.js with the rest of
 * assets/js/*.js and must not depend on cyberverso.js.
 */
(function () {
    'use strict';

    if (!window.fetch || !window.URLSearchParams || !window.history || !Element.prototype.closest) {
        return;
    }

    var API = window.location.origin + '/members/api/';

    // Parameters Ghost appends when sending a reader back to the
    // site. uuid and key must not stay in the address bar, history
    // or Referer. The inline script normally removes them before we
    // run; this list is the belt for anything it missed (hash-only
    // navigations, future params added to the inline allow-list).
    var TRANSIENT_PARAMS = ['action', 'success', 'uuid', 'key', 'newsletter', 'comments',
        'updatesandannouncements', 'errorCode'];

    /* ---------------------------------------------------------------- api */

    function api(path, options) {
        var opts = options || {};
        opts.credentials = 'same-origin';
        if (opts.body && typeof opts.body !== 'string') {
            opts.headers = {'Content-Type': 'application/json'};
            opts.body = JSON.stringify(opts.body);
        }
        return window.fetch(API + path, opts);
    }

    function expectJson(res) {
        if (!res.ok) {
            var error = new Error('HTTP ' + res.status);
            error.status = res.status;
            throw error;
        }
        return res.status === 204 ? null : res.json();
    }

    // auth is {uuid, key} from an email link, or {} to use the session.
    function authQuery(auth) {
        if (!auth || !auth.uuid) {
            return '';
        }
        return '?uuid=' + encodeURIComponent(auth.uuid) + '&key=' + encodeURIComponent(auth.key || '');
    }

    function getSubscriptions(auth) {
        return api('member/newsletters/' + authQuery(auth)).then(expectJson);
    }

    function putSubscriptions(auth, body) {
        return api('member/newsletters/' + authQuery(auth), {method: 'PUT', body: body}).then(expectJson);
    }

    function asIds(list) {
        var out = [];
        for (var i = 0; list && i < list.length; i++) {
            out.push({id: list[i].id});
        }
        return out;
    }

    /* --------------------------------------------------------- module */

    function bindModule(root) {
        var input = root.querySelector('[data-cv-newsletter-email]');
        var button = root.querySelector('[data-cv-newsletter-send]');
        var trap = root.querySelector('[data-cv-newsletter-trap]');
        var message = root.querySelector('[data-cv-newsletter-message]');

        if (!input || !button) {
            return;
        }

        // The controls ship disabled so that nothing is usable until
        // this handler exists.
        input.disabled = false;
        button.disabled = false;

        function setState(value) {
            root.setAttribute('data-cv-state', value);
        }

        function say(kind) {
            if (!message) {
                return;
            }
            message.textContent = kind ? (root.getAttribute('data-cv-msg-' + kind) || '') : '';
            message.setAttribute('data-cv-tone', kind === 'sent' ? 'info' : 'error');
        }

        function submit() {
            if (root.getAttribute('data-cv-state') === 'loading') {
                return;
            }

            var email = (input.value || '').trim();
            if (!email || !input.checkValidity() || email.indexOf('@') === -1) {
                setState('error');
                say('invalid');
                input.focus();
                return;
            }

            setState('loading');
            say('');

            api('integrity-token/')
                .then(function (res) {
                    return res.ok && res.status !== 204 ? res.text() : '';
                })
                .then(function (token) {
                    var body = {
                        email: email,
                        emailType: 'signup',
                        honeypot: trap ? trap.value : ''
                    };
                    var names = root.getAttribute('data-cv-newsletters');

                    if (token) {
                        body.integrityToken = token;
                    }
                    if (names) {
                        body.newsletters = names.split('|').map(function (name) {
                            return {name: name};
                        });
                    }
                    // Deliberately absent: name, labels, urlHistory.
                    return api('send-magic-link/', {method: 'POST', body: body});
                })
                .then(function (res) {
                    if (res.ok) {
                        setState('success');
                        say('sent');
                        input.value = '';
                        return;
                    }
                    setState('error');
                    if (res.status === 429) {
                        say('throttled');
                    } else if (res.status === 400) {
                        say('invalid');
                    } else {
                        say('generic');
                    }
                })['catch'](function () {
                    setState('error');
                    say('generic');
                });
        }

        button.addEventListener('click', submit);
        input.addEventListener('keydown', function (event) {
            if (event.key === 'Enter') {
                event.preventDefault();
                submit();
            }
        });
    }

    /* -------------------------------------------------------- status strip */

    function createPanel(el) {
        var text = el.querySelector('[data-cv-nl-text]');
        var buttons = el.querySelectorAll('[data-cv-nl-act]');
        var handlers = {};

        function render(template, email) {
            var parts = template.split('%email%');
            text.textContent = '';
            for (var i = 0; i < parts.length; i++) {
                if (i > 0) {
                    var strong = document.createElement('strong');
                    strong.textContent = email || '';
                    text.appendChild(strong);
                }
                text.appendChild(document.createTextNode(parts[i]));
            }
        }

        var panel = {
            show: function (key, email, actions) {
                var allowed = actions || [];
                render(el.getAttribute('data-cv-msg-' + key) || '', email);
                for (var i = 0; i < buttons.length; i++) {
                    var act = buttons[i].getAttribute('data-cv-nl-act');
                    buttons[i].hidden = act !== 'close' && allowed.indexOf(act) === -1;
                }
                el.removeAttribute('aria-busy');
                el.hidden = false;
            },
            hide: function () {
                el.hidden = true;
            },
            on: function (act, fn) {
                handlers[act] = fn;
            },
            signedIn: el.getAttribute('data-cv-signed-in') === 'true',
            defaults: (function () {
                var node = el.querySelector('[data-cv-nl-defaults]');
                var raw = node ? node.getAttribute('data-cv-nl-defaults') : '';
                return raw.split(/\s+/).filter(Boolean).map(function (id) {
                    return {id: id};
                });
            }())
        };

        el.addEventListener('click', function (event) {
            var button = event.target.closest('[data-cv-nl-act]');
            if (!button) {
                return;
            }
            var act = button.getAttribute('data-cv-nl-act');
            if (act === 'close') {
                panel.hide();
                return;
            }
            if (handlers[act]) {
                el.setAttribute('aria-busy', 'true');
                handlers[act]();
            }
        });

        return panel;
    }

    function fail(panel) {
        return function () {
            panel.show('error', '', []);
        };
    }

    // Unsubscribe link from an email: /unsubscribe/?uuid=..&key=..
    // is redirected by Ghost to /?uuid=..&key=..&action=unsubscribe.
    // As in Portal, the unsubscribe happens on arrival, with undo.
    function unsubscribeFlow(panel, params) {
        var auth = {uuid: params.get('uuid'), key: params.get('key')};
        var only = params.get('newsletter');
        var updates = params.get('updatesandannouncements');
        var before = null;

        getSubscriptions(auth)
            .then(function (member) {
                if (!member) {
                    throw new Error('member not found');
                }
                before = member;
                if (updates) {
                    return putSubscriptions(auth, {enable_updates_and_announcements: false});
                }
                var keep = (member.newsletters || []).filter(function (n) {
                    return only ? n.uuid !== only : false;
                });
                return putSubscriptions(auth, {newsletters: asIds(keep)});
            })
            .then(function (member) {
                var canUndo = updates || (before.newsletters && before.newsletters.length);
                panel.show('unsubscribed', member.email, canUndo ? ['resubscribe'] : []);
                panel.on('resubscribe', function () {
                    var body = updates ?
                        {enable_updates_and_announcements: true} :
                        {newsletters: asIds(before.newsletters)};
                    putSubscriptions(auth, body).then(function (m) {
                        panel.show('resubscribed', m.email, []);
                    }, fail(panel));
                });
            })['catch'](function () {
                panel.show('invalid-link', '', []);
            });
    }

    // Magic-link landing. Reads the member with the session, shows
    // the outcome, then CLOSES the session: a newsletter-only site
    // has no use for authenticated readers, and while the session
    // lives ghost_head injects the member identifiers into the
    // analytics tag on every page (the inline neutralisation blanks
    // them, but the session itself has no purpose left). The READ
    // must happen BEFORE the DELETE.
    function confirmFlow(panel, afterSignup) {
        function closeSession() {
            return api('session/', {method: 'DELETE'})['catch'](function () {
                // best effort: if it fails, staleSessionFlow retries
                // on the next load, and the session expires anyway
            });
        }

        getSubscriptions({})
            .then(function (member) {
                if (!member) {
                    throw new Error('no session');
                }
                var subscribed = member.newsletters && member.newsletters.length;
                // No action buttons: once the session is closed there
                // is nothing left to do without the emailed link.
                panel.show(
                    subscribed ? (afterSignup ? 'confirmed' : 'account-subscribed') : 'account-unsubscribed',
                    member.email,
                    []
                );
                panel.signedIn = false;
                return closeSession();
            })['catch'](function () {
                panel.show(afterSignup ? 'confirmed' : 'no-accounts', '', []);
                return closeSession();
            });
    }

    // No flow in progress but the page was rendered for a member: a
    // leftover session, closed silently. If the DELETE fails the
    // attempt repeats on every page load until it succeeds — that is
    // the intended behaviour, not a bug: the session must not outlive
    // its purpose, and a failed logout costs nothing to retry.
    function staleSessionFlow(panel) {
        api('session/', {method: 'DELETE'})['catch'](function () {});
        panel.signedIn = false;
    }

    // Leftover Portal entry points (#/portal/..., data-portal).
    function portalIntent(panel, intent) {
        if (/^signup/.test(intent)) {
            var input = document.getElementById('cv-newsletter-email');
            if (input) {
                input.scrollIntoView({block: 'center'});
                input.focus({preventScroll: true});
            } else {
                window.location.href = '/#cv-newsletter';
            }
            return;
        }
        panel.show('no-accounts', '', []);
    }

    // The inline allow-list in default.hbs stashes the email-flow
    // parameters in window.__cvNl before stripping them from the URL
    // (so they never reach the analytics payload). Merge that stash
    // over whatever survives in location.search.
    function readParams() {
        var params = new URLSearchParams(window.location.search);
        var stash = window.__cvNl || {};
        for (var key in stash) {
            if (Object.prototype.hasOwnProperty.call(stash, key)) {
                params.set(key, stash[key]);
            }
        }
        return params;
    }

    function cleanUrl(dropHash) {
        var url = new URL(window.location.href);
        var changed = false;
        TRANSIENT_PARAMS.forEach(function (name) {
            if (url.searchParams.has(name)) {
                url.searchParams['delete'](name);
                changed = true;
            }
        });
        if (dropHash && url.hash) {
            url.hash = '';
            changed = true;
        }
        if (changed) {
            window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
        }
    }

    function initStatus() {
        var el = document.querySelector('[data-cv-nl-status]');
        if (!el) {
            return;
        }

        var panel = createPanel(el);
        var params = readParams();
        var action = params.get('action');
        var hash = window.location.hash || '';

        if (action === 'unsubscribe' && params.get('uuid') && params.get('key')) {
            unsubscribeFlow(panel, params);
            cleanUrl(false);
        } else if ((action === 'signup' || action === 'subscribe' || action === 'signin') && params.has('success')) {
            if (params.get('success') === 'true') {
                confirmFlow(panel, action !== 'signin');
            } else {
                panel.show('expired', '', []);
            }
            cleanUrl(false);
        } else if (hash.indexOf('#/portal') === 0) {
            portalIntent(panel, hash.replace(/^#\/portal\/?/, ''));
            cleanUrl(true);
        } else if (panel.signedIn) {
            staleSessionFlow(panel);
        }

        document.addEventListener('click', function (event) {
            var trigger = event.target.closest('[data-portal], a[href*="#/portal"]');
            if (!trigger) {
                return;
            }
            event.preventDefault();
            var intent = trigger.getAttribute('data-portal') ||
                (trigger.getAttribute('href') || '').replace(/^.*#\/portal\/?/, '');
            portalIntent(panel, intent);
        });
    }

    /* --------------------------------------------------------------- boot */

    function init() {
        var modules = document.querySelectorAll('[data-cv-newsletter]');
        for (var i = 0; i < modules.length; i++) {
            bindModule(modules[i]);
        }
        initStatus();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
}());
